import React from 'react';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import PersonIcon from '@mui/icons-material/PersonOutline';
import SmsIcon from '@mui/icons-material/SmsOutlined';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import CreateIcon from '@mui/icons-material/Create';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RefreshIcon from '@mui/icons-material/Refresh';
import FolderIcon from '@mui/icons-material/Folder';
import SystemUpdateIcon from '@mui/icons-material/SystemUpdate';
import HomeIcon from '@mui/icons-material/Home';
import WorkIcon from '@mui/icons-material/Work';
import LogoutIcon from '@mui/icons-material/Logout';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import { Icon } from '@iconify/react';
import outlineSettings from '@iconify/icons-ic/outline-settings';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import fax16Filled from '@iconify/icons-fluent/fax-16-filled';
import clipboardCheckOutline from '@iconify/icons-mdi/clipboard-check-outline';
import clipboardCheckMultipleOutline from '@iconify/icons-mdi/clipboard-check-multiple-outline';
import attachmentIcon from '@iconify/icons-mdi/paperclip';

// Notification type icons
export const NOTIFICATION_ICONS = {
  email: {
    component: MarkEmailReadIcon,
    props: { fontSize: 'small' },
  },
  documents: {
    component: DescriptionIcon,
    props: { fontSize: 'small' },
  },
  historyEdu: {
    component: HistoryEduIcon,
    props: { fontSize: 'small' },
  },
  info: {
    component: InfoOutlinedIcon,
    props: { fontSize: 'small' },
  },
  bolt: {
    component: BoltOutlinedIcon,
    props: { fontSize: 'small' },
  },
  fileOwner: {
    component: PersonIcon,
    props: { fontSize: 'small' },
  },
  sms: {
    component: SmsIcon,
    props: { fontSize: 'small' },
  },
  fax: {
    component: Icon,
    props: { icon: fax16Filled, fontSize: '20px' },
  },
  statusNote: {
    component: CreateIcon,
    props: { fontSize: 'small' },
  },
  communication: {
    component: ContactMailIcon,
    props: { fontSize: 'small' },
  },
  property: {
    component: BusinessIcon,
    props: { fontSize: 'small' },
  },
  watching: {
    component: VisibilityIcon,
    props: { fontSize: 'small' },
  },
  system: {
    component: SystemUpdateIcon,
    props: { fontSize: 'small' },
  },
  attachment: {
    component: Icon,
    props: { icon: attachmentIcon, fontSize: '16px' },
  },
  checklist: {
    component: CreateIcon,
    props: { fontSize: 'small' },
  },
} as const;

// Filter icons
export const FILTER_ICONS = {
  all: {
    component: NotificationsIcon,
    props: { fontSize: 'small' },
  },
  read: {
    component: CheckCircleIcon,
    props: { fontSize: 'small' },
  },
  unread: {
    component: RadioButtonUncheckedIcon,
    props: { fontSize: 'small' },
  },
  single: {
    component: Icon,
    props: { icon: clipboardCheckOutline, fontSize: '20px' },
  },
  bulk: {
    component: Icon,
    props: { icon: clipboardCheckMultipleOutline, fontSize: '20px' },
  },
  recert: {
    component: EventRepeatIcon,
    props: { fontSize: 'small' },
  },
} as const;

// Action icons
export const ACTION_ICONS = {
  edit: {
    component: EditIcon,
    props: { fontSize: 'small' },
  },
  refresh: {
    component: RefreshIcon,
    props: { fontSize: 'small' },
  },
  folder: {
    component: FolderIcon,
    props: { fontSize: 'small' },
  },
  help: {
    component: HelpOutlineIcon,
    props: { fontSize: 'medium' },
  },
  settings: {
    component: SettingsIcon,
    props: { fontSize: 'large' },
  },
  settingsIconify: {
    component: Icon,
    props: { icon: outlineSettings },
  },
  filterList: {
    component: Icon,
    props: { icon: roundFilterList },
  },
  home: {
    component: HomeIcon,
    props: { fontSize: 'small' },
  },
  workbench: {
    component: WorkIcon,
    props: { fontSize: 'small' },
  },
  admin: {
    component: AdminPanelSettingsIcon,
    props: { fontSize: 'small' },
  },
  logout: {
    component: LogoutIcon,
    props: { fontSize: 'small' },
  },
} as const;

// Header icons
export const HEADER_ICONS = {
  property: {
    component: BusinessIcon,
    props: { fontSize: 'large' },
  },
  fileOwner: {
    component: AdminPanelSettingsIcon,
    props: { fontSize: 'large' },
  },
  watched: {
    component: VisibilityIcon,
    props: { fontSize: 'large' },
  },
  help: {
    component: HelpOutlineIcon,
    props: { fontSize: 'large' },
  },
  family: {
    component: FamilyRestroomIcon,
    props: { fontSize: 'large' },
  },
} as const;

// Communication preference icons
export const COMMUNICATION_ICONS = {
  email: {
    component: EmailIcon,
    props: { fontSize: 'small' },
  },
  sms: {
    component: SmsIcon,
    props: { fontSize: 'small' },
  },
} as const;

// Type definitions
export type NotificationIconType = keyof typeof NOTIFICATION_ICONS;
export type FilterIconType = keyof typeof FILTER_ICONS;
export type ActionIconType = keyof typeof ACTION_ICONS;
export type HeaderIconType = keyof typeof HEADER_ICONS;
export type CommunicationIconType = keyof typeof COMMUNICATION_ICONS;

// Helper function to get icon component
export const getIconComponent = (
  category: 'notification' | 'filter' | 'action' | 'header' | 'communication',
  type: string
): React.ComponentType<Record<string, unknown>> | null => {
  switch (category) {
    case 'notification':
      return (
        NOTIFICATION_ICONS[type as NotificationIconType]?.component || null
      );
    case 'filter':
      return FILTER_ICONS[type as FilterIconType]?.component || null;
    case 'action':
      return ACTION_ICONS[type as ActionIconType]?.component || null;
    case 'header':
      return HEADER_ICONS[type as HeaderIconType]?.component || null;
    case 'communication':
      return (
        COMMUNICATION_ICONS[type as CommunicationIconType]?.component || null
      );
    default:
      return null;
  }
};

// Helper function to get icon props (without styling)
export const getIconProps = (
  category: 'notification' | 'filter' | 'action' | 'header' | 'communication',
  type: string
): Record<string, unknown> => {
  switch (category) {
    case 'notification':
      return NOTIFICATION_ICONS[type as NotificationIconType]?.props || {};
    case 'filter':
      return FILTER_ICONS[type as FilterIconType]?.props || {};
    case 'action':
      return ACTION_ICONS[type as ActionIconType]?.props || {};
    case 'header':
      return HEADER_ICONS[type as HeaderIconType]?.props || {};
    case 'communication':
      return COMMUNICATION_ICONS[type as CommunicationIconType]?.props || {};
    default:
      return {};
  }
};

// Simple icon renderer (no styling included)
export const renderIcon = (
  category: 'notification' | 'filter' | 'action' | 'header' | 'communication',
  type: string,
  props?: Record<string, unknown>
): React.ReactElement | null => {
  const Component = getIconComponent(category, type);
  if (!Component) {
    return null;
  }

  const iconProps = getIconProps(category, type);
  const finalProps = { ...iconProps, ...props };

  if (Component === Icon && 'icon' in iconProps) {
    // Iconify's Icon is not an MUI component and ignores `sx`. Wrap it in a
    // Box so theme tokens (e.g. text.primary) resolve and the SVG inherits
    // color/font-size via currentColor.
    const { sx, ...rest } = finalProps as { sx?: SxProps<Theme> } & Record<
      string,
      unknown
    >;
    const element = <Component icon={iconProps.icon} {...rest} />;
    if (sx) {
      return (
        <Box
          component="span"
          sx={[{ display: 'inline-flex' }, ...(Array.isArray(sx) ? sx : [sx])]}
        >
          {element}
        </Box>
      );
    }
    return element;
  }

  return <Component {...finalProps} />;
};
